import { useAITeacher3d } from "../../../hooks";
import { ENV } from "../../../Utils";
import {
  CameraControls,
  Environment,
  Float,
  Gltf,
  Html,
  Loader,
  useGLTF,
} from "@react-three/drei";
import { Teacher3d } from "../Teacher3d";
import { Leva, button, useControls } from "leva";
import { Suspense, useEffect, useRef } from "react";
import { TypingBox3d } from "../TypingBox3d";
import { MessagesList3d } from "../MessagesList3d";
import { Canvas } from "@react-three/fiber";
import "./Experience3d.scss";
import { degToRad } from "three/src/math/MathUtils.js";
import { BoardSettings3d } from "../BoardSettings3d";

const itemPlacement = {
  default: {
    classroom: {
      position: [0.2, -1.7, -2],
      rotation: [0, degToRad(-90), 0],
    },
    teacher: {
      position: [-1, -1.7, -3],
    },
    board: {
      position: [0.45, 0.382, -6],
    },
  },
  alternative: {
    classroom: {
      position: [0.3, -1.7, -1.5],
      rotation: [0, degToRad(-90), 0],
      scale: 0.4,
    },
    teacher: { position: [-1, -1.7, -3] },
    board: { position: [1.4, 0.84, -8] },
  },
};

export const Experience3d = ({
  accessToken,
  threadId,
  assistantId,
  useAccessToken = true,
}) => {
  const initialize = useAITeacher3d((state) => state.initialize);
  const teacher = useAITeacher3d((state) => state.teacher);
  const classroom = useAITeacher3d((state) => state.classroom);

  useEffect(() => {
    if (useAccessToken) {
      initialize({ accessToken, threadId, assistantId });
    } else {
      initialize({ threadId, assistantId });
    }
  }, [initialize, accessToken, threadId, assistantId, useAccessToken]);

  return (
    <>
      <div className="scene-threejs-assistance">
        <div
          style={{ top: "300px" }}
          className="z-10 md:justify-center absolute bottom-0 left-4 right-4 flex gap-3 flex-wrap justify-stretch"
        >
          <TypingBox3d />
        </div>
        <Leva hidden />
        <Loader />
        <Canvas
          camera={{
            position: [0, 0, 0.0001],
          }}
        >
          <CameraManager />

          <Suspense>
            <Float speed={0.5} floatIntensity={0.2} rotationIntensity={0.1}>
              <Html
                transform
                {...itemPlacement[classroom].board}
                distanceFactor={1}
              >
                <MessagesList3d />
                <BoardSettings3d />
              </Html>
              <Environment preset="sunset" />
              <ambientLight intensity={0.8} color="pink" />

              <Gltf
                src={`${ENV.PATH_GLB}classroom_${classroom}.glb`}
                {...itemPlacement[classroom].classroom}
              />
              <Teacher3d
                teacher={teacher}
                key={teacher}
                {...itemPlacement[classroom].teacher}
                scale={1.5}
                rotation-y={degToRad(20)}
              />
            </Float>
          </Suspense>
        </Canvas>
      </div>
    </>
  );
};
const CAMERA_POSITIONS = {
  default: [0, 6.123233995736766e-21, 0.0001],
  loading: [
    0.00002621880610890309, 0.00000515037441056466, 0.00009636414192870058,
  ],
  speaking: [0, -1.6481333940859815e-7, 0.00009999846226827279],
};

const CAMERA_ZOOMS = {
  default: 1,
  loading: 1.3,
  speaking: 2.1204819420055387,
};

const CameraManager = () => {
  const controls = useRef();
  const loading = useAITeacher3d((state) => state.loading);
  const currentMessage = useAITeacher3d((state) => state.currentMessage);

  useEffect(() => {
    if (loading) {
      controls.current?.setPosition(...CAMERA_POSITIONS.loading, true);
      controls.current?.zoomTo(CAMERA_ZOOMS.loading, true);
    } else if (currentMessage) {
      controls.current?.setPosition(...CAMERA_POSITIONS.speaking, true);
      controls.current?.zoomTo(CAMERA_ZOOMS.speaking, true);
    }
  }, [loading, currentMessage]);

  useControls("Helper", {
    getCameraPosition: button(() => {
      const position = controls.current.getPosition();
      const zoom = controls.current.camera.zoom;
      console.log([...position], zoom);
    }),
  });

  return (
    <CameraControls
      ref={controls}
      minZoom={1}
      maxZoom={3}
      polarRotateSpeed={-0.3} // REVERSE FOR NATURAL EFFECT
      azimuthRotateSpeed={-0.3} // REVERSE FOR NATURAL EFFECT
      mouseButtons={{
        left: 1, //ACTION.ROTATE
        wheel: 16, //ACTION.ZOOM
      }}
      touches={{
        one: 32, //ACTION.TOUCH_ROTATE
        two: 512, //ACTION.TOUCH_ZOOM
      }}
    />
  );
};

useGLTF.preload("/models/classroom_default.glb");
useGLTF.preload("/models/classroom_alternative.glb");
